import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Menu, MenuItem, Typography } from "@material-ui/core";
import { useHistory } from "react-router";

import {
  CustomIconButton,
  CustomCopyToClipboard,
  CustomBadge,
} from "../../../../../Components";
import { useSelectedTheme } from "../../../../../Hooks";
import {
  getDownloadableLink,
  formatCommasForPriceUnit,
} from "../../../../../Helper";

// Icons
import {
  ArrowUpRight,
  UnitSizeIcon,
  BedIcon,
  BathIcon,
  EmptyUnitGalleryIcon,
  MoreVertIcon,
  TrendUpIcon01,
  MarkerPinIcon01,
  PrintIcon,
  Download01,
  Flag05Icon,
} from "../../../../../assets/icons";

// Styles
import useStyles from "./styles";
import Tooltip from "../../../../../Components/V2/Tooltip";
import moment from "moment";

function TransactionCard({ transaction }) {
  const styles = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    theme: { palette },
  } = useSelectedTheme();

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorEl({
      top: rect.top + 25,
      left: rect.left + 15,
    });
  };

  const options = [
    {
      label: "Print Receipt",
      icon: (
        <PrintIcon
          width="16"
          height="16"
          fill={palette.foreground.quarterary}
        />
      ),
      handleOnclick: () => {},
    },
    {
      label: "Print invoices",
      icon: (
        <PrintIcon
          width="16"
          height="16"
          fill={palette.foreground.quarterary}
        />
      ),
      handleOnclick: () => {},
    },
    {
      label: "Download",
      icon: (
        <Download01
          width="16"
          height="16"
          fill={palette.foreground.quarterary}
        />
      ),
      handleOnclick: () => {},
    },
  ];

  const navigate = useHistory();
  const goToUnitDetails = () => {
    navigate.push(
      `/home/units-sales/unit-profile-edit?formType=1&id=${transaction?.unitId}&operationType=430`
    );
  };
  const roi =
    transaction?.roi >= 1
      ? `${transaction?.roi}%`
      : `${transaction?.roi * 100}%`;

  return (
    <Box className={styles.unitCard}>
      <Box className={styles.imageWrapper}>
        <CustomBadge
          label={transaction?.status || "N/A"}
          Style={{
            padding: "4px 12px",
            height: "30px",
            marginLeft: "5px",
            position: "absolute",
            top: "10px",
            right: "10px",
            fontSize: "14px",
            fontWeight: 500,
            border: "1px solid #ABEFC6",
          }}
          SizeVariant={"large"}
          BackgroundColor={"#ffffffb3"}
          BorderColor={palette.utility.success_200}
          Color={palette.utility.success_700}
        />
        <CustomBadge
          label={
            <Box display="flex" alignItems="center" gridGap="8px">
              <Typography
                style={{ color: palette.text.primary, fontWeight: 700 }}
              >
                AED {formatCommasForPriceUnit(transaction?.price)}
              </Typography>
              <TrendUpIcon01 fill={palette.utility.success_500} />
              <Typography
                style={{ color: palette.text.success_primary, fontWeight: 500 }}
              >
                {transaction?.roi ? roi : "N/A"}
              </Typography>
            </Box>
          }
          color={transaction?.unitStatus?.value === "sale" ? "blue" : "red"}
          SizeVariant={"large"}
          Style={{
            padding: "4px 12px",
            height: "30px",
            marginLeft: "5px",
            position: "absolute",
            bottom: "10px",
            left: "10px",
            fontSize: "14px",
            fontWeight: 500,
            border: "1px solid #ABEFC6",
            borderRadius: "8px",
          }}
          BackgroundColor={"#ffffffb3"}
          BorderColor={palette.utility.utility_gray_200}
          Color={palette.utility.utility_gray_700}
        />
        {transaction?.unitImage ? (
          <Box
            className={styles.unitImage}
            style={{
              backgroundImage: `url(${getDownloadableLink(
                transaction?.unitImage.fileId
              )})`,
            }}
          />
        ) : (
          <Box className={styles.emptyImage}>
            <EmptyUnitGalleryIcon
              width="52"
              height="52"
              fill={palette.foreground.quinary}
            />
          </Box>
        )}
      </Box>

      <Box>
        <Box className={styles.details}>
          <Box className={styles.header}>
            <Box
              className={styles.menuWrapper}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <CustomIconButton
                variant="text"
                size="xs"
                boxShadow="none"
                color="secondary"
                onClick={goToUnitDetails}
              >
                <ArrowUpRight
                  width="20"
                  height="20"
                  fill={palette.button.secondaryGray_fg}
                />
              </CustomIconButton>
            </Box>
            <Box>
              <Box className={styles.unitInfo}>
                <CustomCopyToClipboard
                  data={transaction?.unitNumber} // copy
                  childrenData={"#" + transaction?.unitNumber} // render
                  copyDoneShowTime={1000}
                />
              </Box>
              <Box className={styles.unitNameWrapper}>
                {transaction?.ownershipFlag && (
                  <Tooltip text="Currently Owned">
                    <Flag05Icon
                      width={24}
                      height={24}
                      fill={palette.background.brand_solid}
                    />
                  </Tooltip>
                )}
                <Typography className={styles.unitName} component="span">
                  {transaction?.propertyName}
                </Typography>
              </Box>
              <Box
                style={{
                  paddingBottom: "12px",
                }}
                className={styles.unitInfo}
              >
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {transaction?.unitType}
                </Typography>
                <CustomCopyToClipboard
                  data={transaction?.unitRefNumber} // copy
                  childrenData={"#" + transaction?.unitRefNumber} // render
                  copyDoneShowTime={1000}
                />
              </Box>
              <Box className={styles.location}>
                <MarkerPinIcon01
                  fill={palette.foreground.quinary}
                  width={20}
                  height={20}
                />
                <Typography style={{ paddingTop: "0" }} variant="body2">
                  {[
                    transaction?.city,
                    transaction?.district,
                    transaction?.community,
                  ].join(", ")}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className={styles.body}>
            <Box className={styles.unitDetails}>
              <Box className={styles.detailItem}>
                <UnitSizeIcon
                  width="20"
                  height="20"
                  fill={palette.foreground.quinary}
                />
                <span>{transaction?.builtupArea || "N/A"} sq.ft</span>
              </Box>
              <Box className={styles.detailItem}>
                <BedIcon
                  width="20"
                  height="20"
                  fill={palette.foreground.quinary}
                />
                <span>{transaction?.bedrooms || "any"}</span>
              </Box>
              <Box className={styles.detailItem}>
                <BathIcon
                  width="20"
                  height="20"
                  fill={palette.foreground.quinary}
                />
                <span>{transaction?.bathrooms || "any"}</span>
              </Box>
            </Box>
            <Box style={{ paddingTop: "8px", display: "flex", gap: "12px", alignItems: "center" }}>
              <Typography style={{ paddingTop: "0" }} variant="body2">
                Handover Date:
              </Typography>
              {transaction?.handoverDate
                ? moment(transaction.handoverDate).format("DD/MM/YYYY")
                : "Not Available"}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

TransactionCard.propTypes = {
  item: PropTypes.object.isRequired,
};

export default TransactionCard;
